import { useState, useEffect, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, Trash2, Edit, Coffee, Sun, Moon, Cookie, 
  Flame, Target, TrendingUp, Settings, Loader2,
  ChevronLeft, ChevronRight, Sparkles, Camera, Search,
  ScanBarcode, X, Check, Database, PenLine
} from "lucide-react";
import { toast } from "sonner";

import { NutritionDaySummary, NutritionDateNav, NutritionMealList } from "@/components/nutrition/NutritionComponents";

const MEAL_TYPES = {
  colazione: { label: "Colazione", icon: Coffee, color: "bg-yellow-100 text-yellow-700" },
  pranzo: { label: "Pranzo", icon: Sun, color: "bg-orange-100 text-orange-700" },
  cena: { label: "Cena", icon: Moon, color: "bg-purple-100 text-purple-700" },
  spuntino: { label: "Spuntino", icon: Cookie, color: "bg-green-100 text-green-700" }
};

const NutritionPage = () => {
  const [meals, setMeals] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [settings, setSettings] = useState({ daily_calorie_goal: 2000 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  
  // Search & Scan state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiFallbackActive, setAiFallbackActive] = useState(false);
  const [aiFallbackQuery, setAiFallbackQuery] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scanningBarcode, setScanningBarcode] = useState(false);
  
  // Manual entry state
  const [manualEntry, setManualEntry] = useState({
    name: "",
    calories: "",
    proteins: "",
    carbs: "",
    fats: "",
    grams: "100"
  });
  
  // Selected foods for meal
  const [selectedFoods, setSelectedFoods] = useState([]);
  
  // Form state
  const [newMeal, setNewMeal] = useState({
    meal_type: "pranzo",
    description: "",
    notes: ""
  });
  
  // Settings form
  const [newGoal, setNewGoal] = useState(2000);
  
  // Camera ref for barcode scanning
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Memoize meals grouped by type
  const mealsByType = useMemo(() => {
    const grouped = {};
    Object.keys(MEAL_TYPES).forEach(type => {
      grouped[type] = meals.filter(m => m.meal_type === type);
    });
    return grouped;
  }, [meals]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Cleanup camera on unmount
  useEffect(() => {
    const currentStream = streamRef.current;
    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, weeklyRes, settingsRes] = await Promise.all([
        api.get(`/nutrition/daily-summary?date=${selectedDate}`),
        api.get('/nutrition/weekly-summary'),
        api.get('/nutrition/settings')
      ]);
      
      setDailySummary(summaryRes.data);
      setMeals(summaryRes.data.meals || []);
      setWeeklySummary(weeklyRes.data);
      setSettings(settingsRes.data);
      setNewGoal(settingsRes.data.daily_calorie_goal || 2000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Search foods in databases with smart AI fallback
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    const query = searchQuery.trim();
    
    // Check if it looks like multiple items (contains "e", ",", "+", "con")
    const multiItemPatterns = /\s+e\s+|\s*,\s*|\s*\+\s*|\s+con\s+/i;
    const isMultipleItems = multiItemPatterns.test(query);
    
    // If multiple items detected, go straight to AI
    if (isMultipleItems) {
      setAiFallbackActive(true);
      setAiFallbackQuery(query);
      toast.info(`Rilevati più alimenti. Uso l'AI per analizzare "${query}"...`, { duration: 3000 });
      await autoAnalyzeWithAI(query);
      return;
    }
    
    setSearching(true);
    setSearchResults([]);
    setAiFallbackActive(false);
    
    try {
      const response = await api.get(`/nutrition/search?query=${encodeURIComponent(query)}&limit=15`);
      
      if (response.data && response.data.length > 0) {
        setSearchResults(response.data);
      } else {
        // No results - auto fallback to AI
        setAiFallbackActive(true);
        setAiFallbackQuery(query);
        toast.info(`"${query}" non trovato nei database. Uso l'AI per stimare i valori...`, { duration: 3000 });
        setSearching(false);
        await autoAnalyzeWithAI(query);
      }
    } catch (error) {
      toast.error("Errore nella ricerca");
    } finally {
      setSearching(false);
    }
  };

  // Auto AI analysis when database search fails
  const autoAnalyzeWithAI = async (query) => {
    setAnalyzing(true);
    try {
      const response = await api.post('/nutrition/analyze', {
        description: query
      });
      
      // Convert AI response to selected foods format
      const aiFoods = (response.data.foods || []).map(f => ({
        ...f,
        source: "ai",
        grams: 100,
        quantity: 1
      }));
      
      if (aiFoods.length > 0) {
        setSelectedFoods([...selectedFoods, ...aiFoods]);
        toast.success(`✨ "${query}" analizzato con AI! I valori sono stime basate su prodotti simili.`, { duration: 4000 });
      } else {
        toast.warning("L'AI non è riuscita a stimare i valori. Prova con una descrizione più dettagliata.");
      }
    } catch (error) {
      toast.error("Errore nell'analisi AI");
    } finally {
      setAnalyzing(false);
      setAiFallbackActive(false);
      setSearchQuery("");
    }
  };

  // Lookup barcode with auto AI fallback
  const handleBarcodeLookup = async (barcode) => {
    if (!barcode.trim()) return;
    
    setScanningBarcode(true);
    try {
      const response = await api.get(`/nutrition/barcode/${barcode}`);
      addFoodToSelection({...response.data, quantity: 1});
      toast.success(`Trovato: ${response.data.name}`);
      setBarcodeInput("");
      setScannerOpen(false);
      stopCamera();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info("Prodotto non nel database. Prova la ricerca per nome.");
      } else {
        toast.error("Errore nella scansione");
      }
    } finally {
      setScanningBarcode(false);
    }
  };

  // Start camera for barcode scanning
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Camera error:", error);
      toast.error("Impossibile accedere alla fotocamera");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Add manual entry to selection
  const handleManualAdd = () => {
    if (!manualEntry.name.trim() || !manualEntry.calories) {
      toast.error("Inserisci almeno nome e calorie");
      return;
    }
    
    const food = {
      name: manualEntry.name.trim(),
      calories: parseFloat(manualEntry.calories) || 0,
      proteins: parseFloat(manualEntry.proteins) || 0,
      carbs: parseFloat(manualEntry.carbs) || 0,
      fats: parseFloat(manualEntry.fats) || 0,
      grams: parseFloat(manualEntry.grams) || 100,
      source: "manual",
      quantity: 1
    };
    
    setSelectedFoods([...selectedFoods, food]);
    setManualEntry({ name: "", calories: "", proteins: "", carbs: "", fats: "", grams: "100" });
    toast.success("Alimento aggiunto!");
  };

  // Add food to selection
  const addFoodToSelection = (food) => {
    const existingIndex = selectedFoods.findIndex(f => f.name === food.name);
    if (existingIndex >= 0) {
      // Increase quantity
      const updated = [...selectedFoods];
      updated[existingIndex].quantity = (updated[existingIndex].quantity || 1) + 1;
      setSelectedFoods(updated);
    } else {
      setSelectedFoods([...selectedFoods, { ...food, quantity: 1, grams: 100 }]);
    }
  };

  // Update food quantity/grams
  const updateFoodInSelection = (index, field, value) => {
    const updated = [...selectedFoods];
    updated[index][field] = parseFloat(value) || 0;
    setSelectedFoods(updated);
  };

  // Remove food from selection
  const removeFoodFromSelection = (index) => {
    setSelectedFoods(selectedFoods.filter((_, i) => i !== index));
  };

  // Calculate totals from selected foods
  const calculateTotals = () => {
    let totalCalories = 0, totalProteins = 0, totalCarbs = 0, totalFats = 0;
    
    selectedFoods.forEach(food => {
      const multiplier = (food.grams || 100) / 100;
      totalCalories += (food.calories || 0) * multiplier;
      totalProteins += (food.proteins || 0) * multiplier;
      totalCarbs += (food.carbs || 0) * multiplier;
      totalFats += (food.fats || 0) * multiplier;
    });
    
    return {
      calories: Math.round(totalCalories),
      proteins: Math.round(totalProteins * 10) / 10,
      carbs: Math.round(totalCarbs * 10) / 10,
      fats: Math.round(totalFats * 10) / 10
    };
  };

  // AI Analysis (fallback)
  const analyzeWithAI = async () => {
    if (!newMeal.description.trim()) {
      toast.error("Descrivi cosa hai mangiato");
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await api.post('/nutrition/analyze', {
        description: newMeal.description
      });
      
      // Convert AI response to selected foods format
      const aiFoods = (response.data.foods || []).map(f => ({
        ...f,
        source: "ai",
        grams: 100,
        quantity: 1
      }));
      
      setSelectedFoods([...selectedFoods, ...aiFoods]);
      toast.success("Analisi AI completata!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'analisi AI");
    } finally {
      setAnalyzing(false);
    }
  };

  // Save meal
  const handleSaveMeal = async () => {
    if (selectedFoods.length === 0) {
      toast.error("Aggiungi almeno un alimento");
      return;
    }
    
    const totals = calculateTotals();
    const description = selectedFoods.map(f => 
      `${f.grams || 100}g ${f.name}`
    ).join(", ");
    
    try {
      const mealData = {
        date: selectedDate,
        meal_type: newMeal.meal_type,
        description: description,
        foods: selectedFoods.map(f => ({
          name: f.name,
          portion: `${f.grams || 100}g`,
          calories: Math.round((f.calories || 0) * (f.grams || 100) / 100),
          proteins: Math.round((f.proteins || 0) * (f.grams || 100) / 100 * 10) / 10,
          carbs: Math.round((f.carbs || 0) * (f.grams || 100) / 100 * 10) / 10,
          fats: Math.round((f.fats || 0) * (f.grams || 100) / 100 * 10) / 10
        })),
        total_calories: totals.calories,
        total_proteins: totals.proteins,
        total_carbs: totals.carbs,
        total_fats: totals.fats,
        notes: newMeal.notes
      };
      
      if (editingMeal) {
        await api.put(`/meals/${editingMeal.meal_id}`, mealData);
        toast.success("Pasto aggiornato!");
      } else {
        await api.post('/meals', mealData);
        toast.success("Pasto registrato!");
      }
      
      resetForm();
      setAddDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await api.delete(`/meals/${mealId}`);
      toast.success("Pasto eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEditMeal = (meal) => {
    setEditingMeal(meal);
    setNewMeal({
      meal_type: meal.meal_type,
      description: meal.description,
      notes: meal.notes || ""
    });
    // Convert meal foods back to selected foods
    setSelectedFoods((meal.foods || []).map(f => ({
      name: f.name,
      calories: f.calories,
      proteins: f.proteins,
      carbs: f.carbs,
      fats: f.fats,
      grams: parseInt(f.portion) || 100,
      quantity: 1,
      source: "saved"
    })));
    setAddDialogOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      await api.post(`/nutrition/settings?daily_calorie_goal=${newGoal}`);
      toast.success("Obiettivo aggiornato!");
      setSettings({ ...settings, daily_calorie_goal: newGoal });
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const resetForm = () => {
    setNewMeal({ meal_type: "pranzo", description: "", notes: "" });
    setSelectedFoods([]);
    setSearchResults([]);
    setSearchQuery("");
    setEditingMeal(null);
    setAiFallbackActive(false);
    setAiFallbackQuery("");
    setManualEntry({ name: "", calories: "", proteins: "", carbs: "", fats: "", grams: "100" });
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const calorieProgress = dailySummary ? (dailySummary.totals.calories / dailySummary.goal) * 100 : 0;
  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-20 lg:pb-8" data-testid="nutrition-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="page-title">Alimentazione</h1>
          <p className="page-subtitle">Monitora i tuoi pasti e le calorie</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsDialogOpen(true)}
            className="rounded-sm"
          >
            <Target className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Obiettivo</span>
          </Button>
          <Button
            size="sm"
            onClick={() => { resetForm(); setAddDialogOpen(true); }}
            className="rounded-sm"
            data-testid="add-meal-btn"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Aggiungi Pasto</span>
          </Button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
        <p className="text-sm text-amber-800">
          <span className="mr-1.5">🍎</span>
          <strong>Nota bene:</strong> I dati qui mostrati sono stime indicative — ogni piatto è diverso! 
          L'obiettivo è aiutarti a <strong>capire meglio cosa mangi</strong>, non a contare ogni singola caloria. 
          Se hai esigenze alimentari specifiche, parlane con un professionista.
        </p>
      </div>

      {/* Date Navigation */}
      <NutritionDateNav selectedDate={selectedDate} navigateDate={navigateDate} isToday={isToday} />

      {/* Daily Summary Card */}
      <NutritionDaySummary dailySummary={dailySummary} settings={settings} calorieProgress={calorieProgress} />

      {/* Meals List */}
      <NutritionMealList
        meals={meals}
        mealsByType={mealsByType}
        onAdd={() => { resetForm(); setAddDialogOpen(true); }}
        onEdit={handleEditMeal}
        onDelete={handleDeleteMeal}
      />

      {/* Add/Edit Meal Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {editingMeal ? "Modifica Pasto" : "Aggiungi Pasto"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Meal Type */}
            <Select
              value={newMeal.meal_type}
              onValueChange={(value) => setNewMeal({ ...newMeal, meal_type: value })}
            >
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MEAL_TYPES).map(([key, { label, icon: Icon }]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <Icon className="w-4 h-4" /> {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Tabs for different input methods */}
            <Tabs defaultValue="search" className="w-full">
              <TabsList className="grid w-full grid-cols-4 h-auto">
                <TabsTrigger value="search" className="text-xs py-2">
                  <Search className="w-3 h-3 mr-1" /> Cerca
                </TabsTrigger>
                <TabsTrigger value="manual" className="text-xs py-2">
                  <PenLine className="w-3 h-3 mr-1" /> Manuale
                </TabsTrigger>
                <TabsTrigger value="barcode" className="text-xs py-2">
                  <ScanBarcode className="w-3 h-3 mr-1" /> Barcode
                </TabsTrigger>
                <TabsTrigger value="ai" className="text-xs py-2">
                  <Sparkles className="w-3 h-3 mr-1" /> AI
                </TabsTrigger>
              </TabsList>
              
              {/* Search Tab */}
              <TabsContent value="search" className="space-y-3 mt-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Cerca (es. pollo, mela) o più alimenti (es. latte e cereali)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="rounded-sm flex-1"
                    disabled={searching || analyzing}
                  />
                  <Button onClick={handleSearch} disabled={searching || analyzing} size="sm" className="rounded-sm">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* AI Analyzing indicator */}
                {analyzing && aiFallbackActive && (
                  <div className="flex items-center justify-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-sm text-sm" data-testid="ai-fallback-indicator">
                    <Sparkles className="w-4 h-4 text-purple-600 animate-pulse" />
                    <span className="text-purple-700">
                      <strong>&ldquo;{aiFallbackQuery}&rdquo;</strong> non trovato nei database. Analisi AI in corso...
                    </span>
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                  </div>
                )}
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1 border rounded-sm p-2">
                    {searchResults.map((food) => (
                      <button
                        key={food.food_id || `${food.name}-${food.source}`}
                        onClick={() => addFoodToSelection(food)}
                        className="w-full text-left p-2 hover:bg-muted rounded-sm flex items-center justify-between text-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{food.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {food.brand && `${food.brand} · `}{food.calories} kcal/100g
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] ml-2 shrink-0">
                          {food.source === "openfoodfacts" ? "OFF" : food.source === "ai" ? "AI" : food.source === "manual" ? "MAN" : "USDA"}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              {/* Manual Entry Tab */}
              <TabsContent value="manual" className="space-y-3 mt-3">
                <div className="space-y-3">
                  <Input
                    placeholder="Nome alimento (es. Petto di pollo)"
                    value={manualEntry.name}
                    onChange={(e) => setManualEntry({ ...manualEntry, name: e.target.value })}
                    className="rounded-sm"
                    data-testid="manual-food-name"
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Calorie (kcal) *</label>
                      <Input
                        type="number"
                        placeholder="es. 165"
                        value={manualEntry.calories}
                        onChange={(e) => setManualEntry({ ...manualEntry, calories: e.target.value })}
                        className="rounded-sm"
                        data-testid="manual-calories"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Porzione (g)</label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={manualEntry.grams}
                        onChange={(e) => setManualEntry({ ...manualEntry, grams: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Proteine (g)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={manualEntry.proteins}
                        onChange={(e) => setManualEntry({ ...manualEntry, proteins: e.target.value })}
                        className="rounded-sm text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Carboidrati (g)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={manualEntry.carbs}
                        onChange={(e) => setManualEntry({ ...manualEntry, carbs: e.target.value })}
                        className="rounded-sm text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Grassi (g)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={manualEntry.fats}
                        onChange={(e) => setManualEntry({ ...manualEntry, fats: e.target.value })}
                        className="rounded-sm text-sm"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleManualAdd}
                    disabled={!manualEntry.name.trim() || !manualEntry.calories}
                    className="w-full rounded-sm"
                    data-testid="manual-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Aggiungi Alimento
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Inserisci manualmente le calorie se conosci già i valori
                </p>
              </TabsContent>
              
              {/* Barcode Tab */}
              <TabsContent value="barcode" className="space-y-3 mt-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Inserisci codice a barre"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleBarcodeLookup(barcodeInput)}
                    className="rounded-sm flex-1 font-mono"
                  />
                  <Button 
                    onClick={() => handleBarcodeLookup(barcodeInput)} 
                    disabled={scanningBarcode}
                    size="sm"
                    className="rounded-sm"
                  >
                    {scanningBarcode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Inserisci il codice a barre del prodotto (es. 8001505005592)
                </p>
              </TabsContent>
              
              {/* AI Tab */}
              <TabsContent value="ai" className="space-y-3 mt-3">
                <Textarea
                  placeholder="Descrivi il pasto (es. 100g pasta al pomodoro, insalata con olio, un bicchiere di vino)"
                  value={newMeal.description}
                  onChange={(e) => setNewMeal({ ...newMeal, description: e.target.value })}
                  className="rounded-sm min-h-20 text-sm"
                />
                <Button
                  onClick={analyzeWithAI}
                  disabled={analyzing || !newMeal.description.trim()}
                  className="w-full rounded-sm"
                  variant="outline"
                >
                  {analyzing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizzando...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Analizza con AI</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  L&apos;AI stima i valori per piatti complessi non presenti nei database
                </p>
              </TabsContent>
            </Tabs>

            {/* Selected Foods */}
            {selectedFoods.length > 0 && (
              <div className="space-y-2 p-3 bg-muted/30 rounded-sm">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Database className="w-4 h-4" /> Alimenti selezionati
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedFoods.map((food, idx) => (
                    <div key={food.food_id || `selected-${food.name}-${idx}`} className="flex items-center gap-2 text-sm bg-white p-2 rounded-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-xs">{food.name}</p>
                      </div>
                      <Input
                        type="number"
                        value={food.grams || 100}
                        onChange={(e) => updateFoodInSelection(idx, 'grams', e.target.value)}
                        className="w-16 h-7 text-xs text-center rounded-sm"
                      />
                      <span className="text-xs text-muted-foreground">g</span>
                      <span className="text-xs font-mono w-12 text-right">
                        {Math.round((food.calories || 0) * (food.grams || 100) / 100)}
                      </span>
                      <button onClick={() => removeFoodFromSelection(idx)} className="text-destructive p-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Totals */}
                <div className="grid grid-cols-4 gap-2 pt-2 border-t text-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Kcal</p>
                    <p className="font-mono text-sm font-semibold">{totals.calories}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Prot</p>
                    <p className="font-mono text-sm text-blue-600">{totals.proteins}g</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Carb</p>
                    <p className="font-mono text-sm text-yellow-600">{totals.carbs}g</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Grassi</p>
                    <p className="font-mono text-sm text-red-600">{totals.fats}g</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <Input
              placeholder="Note (opzionale)"
              value={newMeal.notes}
              onChange={(e) => setNewMeal({ ...newMeal, notes: e.target.value })}
              className="rounded-sm text-sm"
            />

            <Button
              onClick={handleSaveMeal}
              disabled={selectedFoods.length === 0}
              className="w-full rounded-sm"
            >
              {editingMeal ? "Aggiorna Pasto" : "Salva Pasto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Target className="w-5 h-5" /> Obiettivo Calorico
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Calorie giornaliere</label>
              <Input
                type="number"
                value={newGoal}
                onChange={(e) => setNewGoal(parseInt(e.target.value) || 2000)}
                className="rounded-sm text-center text-lg font-mono"
              />
              <p className="text-xs text-muted-foreground text-center">
                Consigliato: 1500-2500 kcal/giorno
              </p>
            </div>
            
            <Button onClick={handleSaveSettings} className="w-full rounded-sm">
              Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NutritionPage;
